import { Box, ButtonBase, Link, SelectChangeEvent, Stack } from '@mui/material'
import Typography from '../../../../components/Typography'
import { Button } from '../../../../components/Button'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { colors } from '../../../../themes/colors'
import { useCampaignContext } from './CampaignContext'
import React, { useEffect } from 'react'
import {
  FreightCalculateRequest,
  FreightCalculateResponse,
  FreightCharge,
  FreightRange,
  SaleAgreementPromo,
  SaleAgreementPromoProduct,
  SiteInfo,
} from './types'
import { formatPrice } from '../../../../util/helpers'
import { ModalActions } from '../../../../components/Modal'
import { divideIntoMonths, getSites, sortCampaignProducts, isLocationInSameIsland } from './utils'
import DropdownList from '../../../../components/Dropdownlist'
import { useMutation } from '@tanstack/react-query'
import { freightCalculateApi } from './api'
import TabHeader from './TabHeader'
import { SelectSiteView } from './PickupSite'
import { AxiosError, AxiosResponse } from 'axios'

interface SearchAddressTabProps {
  config: SaleAgreementPromo
  campaignId: string
}

enum FreightCalculateApiErrorCode {
  InvalidAddress = 'urn:errors:invalid-address',
  MapboxFailed = 'urn:errors:mapbox-call-failed',
  InvalidSite = 'urn:errors:invalid-site',
  InvalidAgreement = 'urn:errors:invalid-agreement',
}

type FreightCalculateApiError = AxiosError<{
  status: number
  type: FreightCalculateApiErrorCode
}>

const SelectProductTab: React.FC<SearchAddressTabProps> = ({ config, campaignId }) => {
  const { state, setState } = useCampaignContext()
  const [error, setError] = React.useState<undefined | string>()
  const isFreightInclusive = !config.freightRequired

  const freightCalculateMutation = useMutation<
    AxiosResponse<FreightCalculateResponse>,
    FreightCalculateApiError,
    FreightCalculateRequest
  >({
    mutationFn: async (payload: FreightCalculateRequest) => {
      return await freightCalculateApi(payload)
    },
    onSuccess: (res) => {
      console.log('success', res)
      setState({ freightCharge: res.data.unitPrice })
      setError(undefined)
    },
    onError: (err) => {
      console.log('error', err)
      if (err instanceof AxiosError) {
        switch (err.response?.data.type) {
          case FreightCalculateApiErrorCode.MapboxFailed:
            setState({ freightCharge: null })
            break
          case FreightCalculateApiErrorCode.InvalidAddress:
            setError('Oops, delivery address and site must be same island')
            break
          default:
            setError('Something went wrong')
        }
      }
    },
  })

  // calculate freight cost on component mounted only
  React.useEffect(() => {
    if (state.freightRangeSelected !== undefined || isFreightInclusive) {
      return
    }

    if (state.siteSelected !== undefined && state.addressSelected !== undefined) {
      freightCalculateMutation.mutate({
        templateAgreementId: campaignId,
        siteId: state.siteSelected.siteId,
        fullAddress: addressSelectedText,
      })
    }
  }, [])

  const sites = React.useMemo(() => {
    const sites = getSites(config.products)

    return sites.filter((site) => {
      if (!state.addressSelected) {
        return false
      }

      if ('Postcode' in state.addressSelected) {
        return isLocationInSameIsland(
          Number(site.sitePostcode),
          Number(state.addressSelected.Postcode),
        )
      }

      return isLocationInSameIsland(
        Number(site.sitePostcode),
        Number(state.addressSelected.zipCode),
      )
    })
  }, [config.products, state.addressSelected])

  useEffect(() => {
    if (state.siteSelected && sites.length > 0) {
      const available = sites.find((e) =>
        isLocationInSameIsland(Number(e.sitePostcode), Number(state.siteSelected?.sitePostcode)),
      )

      if (!available) {
        setState({
          siteSelected: undefined,
        })
      } else {
        const product = config.products.find((p) => p.siteId === config.defaultSiteId)

        // set the default site
        if (product) {
          setState({
            siteSelected: {
              siteId: product.siteId,
              siteName: product.siteName,
              sitePostcode: product.sitePostcode,
            },
          })
        }
      }
    }
  }, [sites])

  const siteSelectedProducts = React.useMemo(() => {
    return sortCampaignProducts(
      config.products.filter((p) => p.siteId === state.siteSelected?.siteId),
    )
  }, [state.siteSelected, config.products])

  const addressSelectedText = React.useMemo(() => {
    let fullAddressText = ''

    if (state.addressSelected === undefined) {
      return fullAddressText
    }

    if ('UniqueId' in state.addressSelected) {
      fullAddressText = state.addressSelected.FullAddress
    } else {
      fullAddressText = state.addressSelected.fullAddress
    }

    return fullAddressText
  }, [state.addressSelected])

  function handleSubmitNextStep() {
    const hasProductSelected = state.productSelected.length > 0
    const freightCostIsCalculated = typeof state.freightCharge === 'number'

    if (hasProductSelected && (isFreightInclusive || freightCostIsCalculated)) {
      setState({
        tabIndex: state.tabIndex + 1,
      })
    }
  }

  function handleSiteSelect(site: SiteInfo) {
    setState({
      siteSelected: site,
      productSelected: [],
      selectDistance: false,
      freightCharge: undefined, // reset for re-caculate
      freightRangeSelected: undefined, // reset for re-caculate
    })

    if (isFreightInclusive) {
      return
    }

    freightCalculateMutation.mutate({
      templateAgreementId: campaignId,
      siteId: site.siteId,
      fullAddress: addressSelectedText,
    })
  }

  function handleOnFreightRangeSelect(freightRange: FreightRange) {
    if (!state.siteSelected) {
      console.error('Error: siteId is undefined')
      return
    }

    freightCalculateMutation.mutate({
      templateAgreementId: campaignId,
      siteId: state.siteSelected.siteId,
      freightRange,
    })

    setState({
      selectDistance: false,
      freightRangeSelected: freightRange,
    })
  }

  if (state.siteSelected === undefined) {
    return <SelectSiteView sites={sites} onSiteSelect={handleSiteSelect} />
  }

  if (state.selectDistance) {
    return (
      <SelectDistanceView
        freightRanges={config.freightRanges}
        onFreightRangeSelect={handleOnFreightRangeSelect}
      />
    )
  }

  return (
    <Stack direction='column' pb='100px'>
      {error ? (
        <Typography variant='caption' color='error500'>
          {error}
        </Typography>
      ) : null}
      <SelectProductView products={siteSelectedProducts} isFreightInclusive={isFreightInclusive} />
      <ModalActions showDivider>
        <Button
          key='next-step-btn'
          variant='contained'
          onClick={handleSubmitNextStep}
          // data-testid={TestIds.feat.product.details.orderButton}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next Step
        </Button>
      </ModalActions>
    </Stack>
  )
}

type CampaignProductProps = {
  detail: SaleAgreementPromoProduct
  isSelected: boolean
  onSelect: (select: boolean, product: SaleAgreementPromoProduct) => void
  onSelectDistance: () => void
  isFreightInclusive: boolean
  freightCharge: FreightCharge
}

const CampaignProduct: React.FC<CampaignProductProps> = ({
  detail,
  isSelected,
  onSelect,
  onSelectDistance,
  isFreightInclusive,
  freightCharge,
}) => {
  const { description, unitPrice, unit } = detail
  const selectedButtonSx = isSelected
    ? {
        '&:hover': {
          backgroundColor: colors.primary100,
        },
        backgroundColor: colors.primary100,
        color: colors.primary500,
        gap: 1,
      }
    : undefined

  const renderFreightCharge = React.useMemo(() => {
    if (isFreightInclusive) {
      return null
    }

    const freightChargeCannotBeCalculate = freightCharge === null
    const freightChargeIsCalculated = typeof freightCharge === 'number'

    if (freightChargeIsCalculated) {
      return <ProductPrice label='Freight' price={freightCharge} />
    }

    if (freightChargeCannotBeCalculate) {
      return (
        <ProductPrice
          label='Freight'
          price={
            <Link onClick={onSelectDistance}>
              <Typography variant='body2' color='primary500'>
                Select Distance
              </Typography>
            </Link>
          }
        />
      )
    }

    return null
  }, [isFreightInclusive, freightCharge])

  return (
    <Stack direction='column' bgcolor='white' border={'1px solid #D5D6DA'} p={2} gap={1}>
      <Typography variant='h4'>{description}</Typography>
      <Stack direction={{ sm: 'column', md: 'row' }} gap={1} justifyContent='space-between'>
        <Stack direction='row'>
          <ProductPrice label='Price' price={unitPrice} unit={unit} />
          {renderFreightCharge}
        </Stack>
        <Box alignSelf='flex-end'>
          <Button
            variant={isSelected ? 'contained' : 'outlined'}
            sx={selectedButtonSx}
            onClick={() => onSelect(!isSelected, detail)}
          >
            {isSelected ? 'Selected' : 'Select'}
            {isSelected ? <MaterialSymbol>done</MaterialSymbol> : null}
          </Button>
        </Box>
      </Stack>
    </Stack>
  )
}

interface ProductPriceProps {
  label: string
  price: number | React.ReactNode
  unit?: string
}

const ProductPrice: React.FC<ProductPriceProps> = ({ label, price, unit }) => {
  return (
    <Stack direction='column' minWidth='160px'>
      <Typography variant='body2' fontWeight='bold'>
        {label}
      </Typography>
      {typeof price === 'number' ? (
        <>
          <Stack direction={'row'} alignItems='end' gap={0.5}>
            <Typography variant='body1' fontWeight='bold' color='secondary700'>
              {formatPrice(price)}
            </Typography>
            {unit && (
              <span>
                <Typography fontSize={'12px'} color='secondary700'>
                  per {unit}
                </Typography>
              </span>
            )}
          </Stack>
          <Typography variant='small' color='secondary700'>
            GST excl.
          </Typography>
        </>
      ) : (
        price
      )}
    </Stack>
  )
}

interface SelectProductViewProps {
  products: SaleAgreementPromoProduct[]
  isFreightInclusive: boolean
}

const SelectProductView: React.FC<SelectProductViewProps> = ({ products, isFreightInclusive }) => {
  const { state, setState } = useCampaignContext()
  const productIdsSelected = React.useMemo(() => {
    return state.productSelected.map((p) => p.itemNumber)
  }, [state.productSelected])

  function handleOnProductSelect(select: boolean, product: SaleAgreementPromoProduct) {
    if (select === false) {
      setState({
        productSelected: state.productSelected.filter((p) => p.itemNumber !== product.itemNumber),
      })
      return
    }

    // implement when select is true
    const productNotAdded =
      state.productSelected.findIndex((p) => p.itemNumber === product.itemNumber) === -1

    if (productNotAdded) {
      product.orders = divideIntoMonths(0, product.startDate, product.endDate)

      setState({
        productSelected: [...state.productSelected, product],
      })
    }
  }

  function handleOnSelectDistance() {
    setState({ selectDistance: true })
  }

  return (
    <Stack direction='column' gap={2}>
      <TabHeader title='Add products' caption='Select products to include in your agreement' />
      <SiteSelection
        isFreightInclusive={isFreightInclusive}
        onSelectDistance={handleOnSelectDistance}
      />
      {products.map((product) => {
        return (
          <CampaignProduct
            key={product.itemNumber}
            detail={product}
            isSelected={productIdsSelected.includes(product.itemNumber)}
            onSelect={handleOnProductSelect}
            onSelectDistance={handleOnSelectDistance}
            isFreightInclusive={isFreightInclusive}
            freightCharge={state.freightCharge}
          />
        )
      })}
    </Stack>
  )
}

interface SiteSelectionProps {
  isFreightInclusive: boolean
  onSelectDistance: () => void
}

const SiteSelection = ({ isFreightInclusive, onSelectDistance }: SiteSelectionProps) => {
  const { state, setState } = useCampaignContext()

  const renderSelectDistance = React.useMemo(() => {
    // freightCharge is not able to calculate
    if (state.freightCharge === null) {
      return (
        <Stack
          direction='row'
          bgcolor={colors.secondary100}
          p={0.5}
          borderRadius='5px'
          width='100%'
          alignItems='center'
          gap={1}
        >
          <MaterialSymbol variant='fill' sx={{ fontSize: '20px', color: colors.secondary900 }}>
            error
          </MaterialSymbol>
          <Typography variant='caption'>
            We're unable to calculate freight cost.{' '}
            <Link onClick={onSelectDistance}>Select distance</Link>
          </Typography>
        </Stack>
      )
    }

    // freight is calculated with distance selected
    if (state.freightRangeSelected && typeof state.freightCharge === 'number') {
      return (
        <Stack
          direction='row'
          bgcolor={colors.primary100}
          p={0.5}
          borderRadius='5px'
          width='100%'
          alignItems='center'
          gap={1}
        >
          <MaterialSymbol variant='fill' sx={{ fontSize: '20px', color: colors.primary900 }}>
            error
          </MaterialSymbol>
          <Typography variant='caption'>
            Estimated distance from site{' '}
            {`${state.freightRangeSelected.fromKm} - ${state.freightRangeSelected?.toKm} km `}
            <Link onClick={onSelectDistance}>Edit distance</Link>
          </Typography>
        </Stack>
      )
    }

    return null
  }, [state.freightCharge, state.freightRangeSelected])

  const renderFreightChartInfo = React.useMemo(() => {
    if (isFreightInclusive) {
      return <Typography variant='caption'>Freight inclusive</Typography>
    }

    if (typeof state.freightCharge === 'number') {
      return (
        <Typography variant='caption'>{`Freight ${formatPrice(state.freightCharge)} per sling (GST excl.)`}</Typography>
      )
    }

    return null
  }, [isFreightInclusive, state.freightCharge])

  function handleReselectSite() {
    setState({ siteSelected: undefined })
  }

  return (
    <Stack direction='column' gap={1}>
      <Stack
        direction='column'
        gap={0.5}
        bgcolor='white'
        border={'1px solid #D5D6DA'}
        px={2}
        py={1.5}
        borderRadius={1}
      >
        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' gap={1}>
            <Typography variant='body2' color='primary500' fontWeight='bold'>
              Site
            </Typography>
            <Typography variant='body2' color='grey800'>
              {state.siteSelected?.siteName}
            </Typography>
          </Stack>
          <ButtonBase
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              alignSelf: 'start',
            }}
            disableRipple
            onClick={handleReselectSite}
          >
            <Typography variant='body2' color='primary500'>
              Edit
            </Typography>
            <MaterialSymbol sx={{ fontSize: '16px', color: colors.primary500 }}>
              edit
            </MaterialSymbol>
          </ButtonBase>
        </Stack>
        {renderFreightChartInfo}
      </Stack>
      {renderSelectDistance}
    </Stack>
  )
}

interface SelectDistanceViewProps {
  freightRanges: FreightRange[]
  onFreightRangeSelect: (value: FreightRange) => void
}

const SelectDistanceView = ({ freightRanges, onFreightRangeSelect }: SelectDistanceViewProps) => {
  const { state, setState } = useCampaignContext()

  const selectionItems = freightRanges.map((fr, index) => ({
    label: `${fr.fromKm}km - ${fr.toKm}km`,
    value: String(index),
  }))

  const handleFreightRangeChange = (event: SelectChangeEvent<string>) => {
    const index = event.target.value
    onFreightRangeSelect(freightRanges[Number(index)])
  }

  return (
    <Stack direction='column' gap={3}>
      <Stack
        direction='row'
        p={1}
        bgcolor={colors.primary100}
        borderRadius={2}
        alignItems='center'
        justifyContent='space-between'
      >
        <Stack direction='column' borderLeft='3px solid #00553D' px={1}>
          <Typography variant='body2' fontWeight='bold' color='primary500'>
            Site
          </Typography>
          <Typography variant='body2' color='primary500'>
            {state.siteSelected?.siteName}
          </Typography>
        </Stack>
        <ButtonBase
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
          disableRipple
          onClick={() => setState({ siteSelected: undefined })}
        >
          <Typography variant='body2' color='primary500'>
            Edit
          </Typography>
          <MaterialSymbol sx={{ fontSize: '16px', color: colors.primary500 }}>edit</MaterialSymbol>
        </ButtonBase>
      </Stack>
      <Stack gap={0.5}>
        <DropdownList
          label={'Distance band'}
          items={selectionItems}
          placeholder='Join'
          onChange={handleFreightRangeChange}
        />
        <Typography variant='body2' color='grey700'>
          Your freight cost is calculated by the number of slings and distance from your nearest
          site.
        </Typography>
      </Stack>
    </Stack>
  )
}

export default SelectProductTab
